import * as PIXI from "pixi.js"
import { NodeData } from "./Model/types"
import Node from "./Model/Node"
import Settings from "./Settings"

export default class TreeGraph extends PIXI.Container {
    rootNode: Node
    lineGraphics: PIXI.Graphics
    app: PIXI.Application
    didZoomChange = false

    constructor(rootNodeData: NodeData, app: PIXI.Application) {
        super()

        this.rootNode = new Node(rootNodeData)
        this.app = app

        this.addNodeToStage(this.rootNode, 1)
        this.layoutNodes(this.rootNode)

        // create line container
        this.lineGraphics = new PIXI.Graphics()
        this.lineGraphics.lineStyle(4, 0xbcc3c9, 1)
        this.addChildAt(this.lineGraphics, 0)
        this.lineGraphics.zIndex = 0
        this.renderLines(this.rootNode)

        document.addEventListener("ZoomDidChange", ((e: CustomEvent) => {
            this.didZoomChange = true
        }) as EventListener)
    }

    initLayout() {
        this.scale.set(0.3, 0.3)
        this.centerOnRootNode()

        this.scaleNodesForCurrentZoomLevel(this.rootNode)
    }

    centerOnRootNode() {
        // force all nodes to root node position
        function initPos(node: Node, target: Node) {
            node.x = target.targetX
            node.y = target.targetY
            for (let child of node.childNodes) {
                initPos(child, target)
            }
        }
        initPos(this.rootNode, this.rootNode)

        // center vertically
        const maxY = this.getMaxY(this.rootNode, -10000)
        const minY = this.getMinY(this.rootNode, 1000000)
        const height = (minY + maxY) * this.scale.y
        this.y = this.app.screen.height / 2 - height / 2 - 50

        // center horizontally
        let offsetX = this.rootNode.targetX
        this.x = -offsetX * this.scale.x + this.app.screen.width / 2
    }

    getMaxY(node: Node, maxY: number) {
        maxY = Math.max(maxY, node.targetY)
        for (const childNode of node.childNodes) {
            maxY = this.getMaxY(childNode, maxY)
        }
        return maxY
    }

    getMinY(node: Node, minY: number) {
        minY = Math.min(minY, node.targetY)
        for (const childNode of node.childNodes) {
            minY = this.getMinY(childNode, minY)
        }
        return minY
    }

    addNodeToStage(node: Node, level: number) {
        // node.zIndex = 1000 - level
        this.addChildAt(node, 0)
        for (const childNode of node.childNodes) {
            this.addNodeToStage(childNode, level + 1)
        }
    }

    update() {
        this.updateNodePosition(this.rootNode)
        this.lineGraphics.clear()
        const lineWeight = 3 / this.scale.x
        this.lineGraphics.lineStyle(lineWeight, 0xbcc3c9, 1)
        this.renderLines(this.rootNode)

        if (this.didZoomChange) {
            this.scaleNodesForCurrentZoomLevel(this.rootNode)
            this.didZoomChange = false
        }
    }

    updateNodePosition(node: Node) {
        for (let i = 0; i < node.childNodes.length; i++) {
            const child = node.childNodes[i]
            this.updateNodePosition(child)
        }
        const damp = Settings.nodeAnimationDamping
        node.x += (node.targetX - node.x) * damp
        node.y += (node.targetY - node.y) * damp
        node.scale.x += (node.targetScale - node.scale.x) * damp
        node.scale.y += (node.targetScale - node.scale.y) * damp
    }

    scaleNodesForCurrentZoomLevel(node: Node) {
        let targetScale = 1 / this.scale.x

        if (this.scale.x > 0.3) {
            // node.targetScale = targetScale
            node.titleText.alpha = 1
            node.titleText.scale.set(1, 1)
            node.titleText.pivot.set(0, 0)
            node.bgBox.scale.set(1, 1)
            node.warningIndicator.scale.set(0, 0)
        } else {
            const scale = 1 / 0.3
            const offset = 0.3 - this.scale.x
            // console.log(offset)
            const newTargetScale = scale + offset * 15
            targetScale = newTargetScale
            // node.targetScale = newTargetScale
            node.titleText.alpha = 1 - offset * 10
            node.titleText.scale.set(1 - offset * 3, 1 - offset * 3)
            node.titleText.pivot.set(0, offset * 200)
            node.bgBox.scale.set(1 - offset * 3, 1 - offset * 2)

            node.propertyIndicator.alpha = 1 - offset * 10
            node.propertyIndicator.scale.set(1 - offset * 3, 1 - offset * 3)
            node.propertyIndicator.pivot.set(0, offset * 500)

            const indicatorScale = Math.min(1, offset * 10)
            // console.log(indicatorScale)
            node.warningIndicator.scale.set(indicatorScale, indicatorScale)
        }

        node.container.scale.set(targetScale, targetScale)
        node.detailSafeZone.scale.y = Math.max(1 - (1 - this.scale.y) * 2.0, 0)

        let indicatorTarget = -12

        const safeHeight = node.detailSafeZone.height
        for (const detailItem of node.detailItems) {
            if (detailItem.y < safeHeight) {
                detailItem.visible = true
                indicatorTarget = detailItem.y + detailItem.height + 10
                node.toggle.y = detailItem.y + 200
            } else {
                detailItem.visible = false
            }
        }

        node.propertyIndicator.y = indicatorTarget
        node.updatePropIndicator()

        for (const childNode of node.childNodes) {
            this.scaleNodesForCurrentZoomLevel(childNode)
        }
    }

    renderLines(node: Node) {
        const curveSize = 60
        const numChildren = node.childNodes.length
        for (let i = 0; i < numChildren; i++) {
            const child = node.childNodes[i]

            // if child is directly under parent, draw straight line
            if (child.x === node.x) {
                this.lineGraphics.moveTo(node.x, node.y)
                this.lineGraphics.lineTo(child.x, child.y)
            } else {
                const dist = child.y - node.y
                const midY = child.y - dist * 0.2

                // hard edges
                // this.lineGraphics.moveTo(node.x, node.y)
                // this.lineGraphics.lineTo(node.x, midY)
                // this.lineGraphics.lineTo(child.x, midY)
                // this.lineGraphics.lineTo(child.x, child.y)

                // curves

                const vertHalfDist = Math.abs(child.y - node.y) / 2
                const horzHalfDist = Math.abs(child.x - node.x) / 2

                const curveOffset = curveSize
                const vertCurveOffset = Math.min(vertHalfDist, curveOffset)
                const horzCurveOffset = Math.min(horzHalfDist, curveOffset)
                const dir = child.x > node.x ? 1 : -1

                this.lineGraphics.moveTo(node.x, node.y)
                this.lineGraphics.lineTo(node.x, midY - vertCurveOffset)
                this.lineGraphics.quadraticCurveTo(node.x, midY, node.x + horzCurveOffset * dir, midY)

                this.lineGraphics.lineTo(child.x - horzCurveOffset * dir, midY)

                this.lineGraphics.quadraticCurveTo(child.x, midY, child.x, midY + vertCurveOffset)

                this.lineGraphics.lineTo(child.x, child.y)
            }

            this.renderLines(child)
        }
    }

    layoutNodes(node: Node) {
        this.resetNodes(node)
        this.calculateInitialPositions(node, 0)
        this.checkAllChildrenOnScreen(node)
        this.centerMiddleLeafNodes(node)
        this.calculateFinalPositions(node, 0, false)
    }

    resetNodes(node: Node) {
        for (let child of node.childNodes) {
            this.resetNodes(child)
        }
        node.targetX = 0
        node.targetY = 0
        // rootNode.x = 0
        // rootNode.y = 0
        node.mod = 0
    }

    calculateInitialPositions(node: Node, depth: number) {
        for (let child of node.childNodes) {
            this.calculateInitialPositions(child, depth + 1)
        }

        // set y based on depth
        node.targetY = depth * Settings.nodeRowSpacing
        const margin = Settings.nodeHorizontalSpacing

        if (node.isLeaf()) {
            if (!node.isLeftMost()) {
                // node doesn't have children and isn't the left-most of its siblings (ie NOT the first child of its parent node),
                // so set its x equal to it's left sibling + NODE_SIZE + SIBLING_DISTANCE.
                const prevSibling = node.getPreviousSibling()
                if (prevSibling !== undefined) {
                    node.targetX = prevSibling.targetX + margin
                }
            } else {
                // node is the left-most of its siblings, if it even has siblings. Set its x equal to 0.
                node.targetX = 0
            }
        } else if (node.getChildCount() === 1) {
            if (node.isLeftMost()) {
                // node has 1 child node and node is the left-most of its siblings, if it even has siblings.
                // Set node's x equal to its only child's x to position it directly above its child.
                const leftMostChild = node.getLeftMostChild()
                if (leftMostChild) {
                    node.targetX = leftMostChild.targetX
                }
            } else {
                // node has 1 child node, but it isn't the left-most of its siblings.
                // Set node's x equal to its left-sibling + NODE_SIZE + SIBLING_DISTANCE.

                const prevSibling = node.getPreviousSibling()
                if (prevSibling) {
                    node.targetX = prevSibling.targetX + margin
                }
                // node's x is positioned to the right of its left-sibling, so its single-child node needs to be
                // shifted underneath its parent. Set node's mod equal to the distance the single-child node needs
                // to shift to be underneath node. Later, we'll use each parent node's mod value to update its child nodes'
                // x values, so they'll all shift underneath their parents.
                const leftMostChild = node.getLeftMostChild()
                if (leftMostChild) {
                    node.mod = node.targetX - leftMostChild.targetX
                }
            }
        } else {
            // node has more than one child nodes, so get the midpoint difference of its left-most child node and its
            // right-most child node.
            const leftMostChild = node.getLeftMostChild()
            const rightMostChild = node.getRightMostChild()
            let childrenNodesMidpointX = 0
            if (leftMostChild && rightMostChild) {
                childrenNodesMidpointX = (leftMostChild.targetX + rightMostChild.targetX) / 2
            }

            if (node.isLeftMost()) {
                // node is the left-most of its siblings, if it even has siblings.
                // Set node's x equal to its children's midpoint value to position it directly above its children.
                node.targetX = childrenNodesMidpointX
            } else {
                // node isn't the left-most of its siblings, so set its x equal to its left-sibling's x + NODE_SIZE + SIBLING_DISTANCE.
                const prevSibling = node.getPreviousSibling()
                if (prevSibling) {
                    node.targetX = prevSibling.targetX + margin
                }

                // node's x is positioned to the right of its left-sibling, so its child nodes need to be
                // shifted underneath their parent. Set node's mod equal to the distance the children nodes needs
                // to shift to be underneath node. Later, we'll use each parent node's mod value to update its child nodes'
                // x values, so the'll all shift underneath their parents.
                node.mod = node.targetX - childrenNodesMidpointX
            }
        }

        if (!node.isLeaf() && !node.isLeftMost()) {
            this.checkForTreeConflicts(node)
        }
    }

    checkForTreeConflicts(node: Node) {
        /*
        Compares the node's left-edge ALL of its sibling's right-edges, from it's left-most-sibling inward,
        to determine if any of its left-sibling's right-edges intersect this node's left-edge. The largest intersection
        value is used to determine how much this node needs to shift to the right so that it's left-edge doesn't intersect
        with any of its left siblings' right-edges. 
        */
        const minimumDistanceBetweenTrees = Settings.minDistBetweenTrees

        var leftSibling = node.getLeftMostSibling()

        if (leftSibling === undefined) {
            return
        }

        let shortestDistanceBetweenTreeContours = minimumDistanceBetweenTrees

        while (leftSibling !== undefined && leftSibling !== node) {
            let leftContour = this.getLeftContour(node, 0, {})
            let rightContour = this.getRightContour(leftSibling, 0, {})

            for (let yLevel = this.minContourKeyValue(leftContour); yLevel <= Math.min(this.maxContourKeyValue(leftContour), this.maxContourKeyValue(rightContour)); yLevel++) {
                if (leftContour[yLevel] - rightContour[yLevel] < shortestDistanceBetweenTreeContours) {
                    shortestDistanceBetweenTreeContours = leftContour[yLevel] - rightContour[yLevel]
                }
            }

            // Iterate to the right.
            leftSibling = leftSibling.getNextSibling()
        }

        if (shortestDistanceBetweenTreeContours < minimumDistanceBetweenTrees) {
            let shiftValue = minimumDistanceBetweenTrees - shortestDistanceBetweenTreeContours
            node.targetX += shiftValue
            node.mod += shiftValue
        }
    }

    getLeftContour(node: Node, modSum: number, values: any) {
        if (values[node.targetY] === undefined) {
            values[node.targetY] = node.targetX + modSum
        } else {
            values[node.targetY] = Math.min(values[node.targetY], node.targetX + modSum)
        }

        if (!node.collapsed) {
            modSum += node.mod

            node.childNodes.forEach((child) => {
                values = this.getLeftContour(child, modSum, values)
            })
        }

        return values
    }

    getRightContour(node: Node, modSum: number, values: any) {
        if (values[node.targetY] === undefined) {
            values[node.targetY] = node.targetX + modSum
        } else {
            values[node.targetY] = Math.max(values[node.targetY], node.targetX + modSum)
        }

        if (!node.collapsed) {
            modSum += node.mod

            node.childNodes.forEach((child) => {
                values = this.getRightContour(child, modSum, values)
            })
        }

        return values
    }

    minContourKeyValue(contour: any) {
        let minValue = 0
        for (const key in contour) {
            if (parseInt(key) < minValue || minValue === undefined) {
                minValue = parseInt(key)
            }
        }
        return minValue
    }

    maxContourKeyValue(contour: any) {
        let maxValue = 0
        for (const key in contour) {
            if (parseInt(key) > maxValue || maxValue === undefined) {
                maxValue = parseInt(key)
            }
        }
        return maxValue
    }

    checkAllChildrenOnScreen(node: Node) {
        /* 
            Get rootNode's left-contour. If any of its left-contour's x values are less than 0,
            set shiftValue equal to the negative x value as the inverse positive x value.
        */
        let leftContour = this.getLeftContour(node, 0, {})
        let shiftAmount = 0
        for (const y in leftContour) {
            if (leftContour[y] + shiftAmount < 0) {
                shiftAmount = leftContour[y] * -1
            }
        }

        // If shiftAmount is greater than 0, increment rootNode's x and mod values by shiftAmount,
        // ensuring the tree isn't positioned beyond the left border of whatever UI panel it'll be
        // rendered in.
        if (shiftAmount > 0) {
            node.targetX += shiftAmount
            node.mod += shiftAmount
        }
    }

    centerMiddleLeafNodes(node: Node) {
        /* 
        This method iterate's node's children, right-to-left. If the iterated child is a leaf and is positioned somewhere 
        in the middle (between it's parent's left-most and right-most child nodes), the iterated child is positioned between
        it's immediate left and right siblings. If the iterated child has children, it isn't centered but this method is called
        recursively for the child's children, as the child's children might have middle positioned leaf nodes that need to be centered,
        so on and so forth.
        */
        for (let i = node.childNodes.length - 1; i >= 0; i--) {
            const child = node.childNodes[i]
            if (child.isLeaf() && !child.isLeftMost() && !child.isRightMost()) {
                const prevSibling = child.getPreviousSibling()
                const nextSibling = child.getNextSibling()
                if (prevSibling && nextSibling) {
                    let leftSiblingX = prevSibling.targetX
                    let rightSiblingX = nextSibling.targetX
                    let centerValue = (rightSiblingX + leftSiblingX) / 2
                    child.targetX = centerValue
                }
            }
            if (!child.isLeaf()) {
                this.centerMiddleLeafNodes(child)
            }
        }
    }

    calculateFinalPositions(node: Node, modSum: number, collapsed: boolean) {
        node.targetX += modSum
        modSum += node.mod

        // const expandedScale = 1 / this.scale.x

        // if any parent is collapsed, render this child as hidden
        if (collapsed) {
            const parent = node.parentNode
            if (parent) {
                node.targetX = parent.targetX
                node.targetY = parent.targetY
                // node.targetScale = expandedScale * 0.3
                node.targetScale = 0.0
            }
        } else {
            node.targetScale = 1
        }

        if (node.collapsed) {
            collapsed = true
            // node.toggle.visible = true
        } else {
            // node.toggle.visible = false
        }

        // Post-order traversal.
        node.childNodes.forEach((child) => {
            this.calculateFinalPositions(child, modSum, collapsed)
        })

        // if (node.isLeaf()) {
        //     node.width = node.x
        //     node.height = node.y
        // } else {
        //     node.width = this.maxChildNodeWidth(node)
        //     node.height = this.maxChildNodeHeight(node)
        // }
    }
}
